import { useLayoutEffect, useRef, useState } from 'react';

import { bind } from 'bind-event-listener';

import { findOverflowScrollParent } from '@atlaskit/editor-common/ui';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import { UNSAFE_expValNoExposure } from '@atlaskit/platform-feature-experiments/unsafe-exp-val-no-exposure';

import { findWhiteboardNodePosByContentId } from '../../editor-commands/replace-whiteboard-embed';
import { getRemixThinkingWidgetContainer } from '../../pm-plugins/remix-thinking/remix-thinking-decoration';

/**
 * Where a floating remix bar should be anchored relative to the viewport when
 * the inline thinking widget is scrolled out of view.
 * - `in-view`: the inline widget is visible, so no floating bar is needed.
 * - `bottom`: the inline widget is out of view — anchor the floating bar to the
 *   bottom of the screen, regardless of scroll direction.
 */
export type ThinkingWidgetViewportPlacement = 'in-view' | 'bottom';

const getViewportPlacement = ({
	element,
	root,
}: {
	element: HTMLElement;
	root: HTMLElement;
}): ThinkingWidgetViewportPlacement => {
	try {
		const elementRect = element.getBoundingClientRect();
		const rootRect = root.getBoundingClientRect();

		const isInViewport =
			elementRect.bottom > rootRect.top &&
			elementRect.right > rootRect.left &&
			elementRect.top < rootRect.bottom &&
			elementRect.left < rootRect.right;

		if (isInViewport) {
			return 'in-view';
		}

		// The floating bar always sits at the bottom of the viewport when the inline
		// widget is scrolled out of view, regardless of scroll direction.
		return 'bottom';
	} catch {
		// On measurement failure, behave as if the widget is in view so we never
		// show a stuck floating bar we cannot position.
		return 'in-view';
	}
};

/**
 * Tracks whether the inline remix thinking widget is within the scroll
 * viewport, and — when it is not — reports `bottom` so a floating bar can stick
 * to the bottom edge. Measures live during the active remix (thinking or
 * complete) and re-measures on scroll / resize.
 *
 * `isActive` should be true whenever a remix is in progress (thinking) or has
 * just completed (showing the ready bar). When inactive, the widget is always
 * reported as `in-view` and no DOM is queried.
 */
export const useThinkingWidgetViewportPlacement = ({
	editorView,
	isActive,
	diagramContentId,
	onDiagramBoardReady,
	onDiagramBoardTimeout,
}: {
	/**
	 * When set, this remix produced a diagram: track the whiteboard node with this contentId
	 * instead of the thinking-widget anchor, so the in-view decision is about the diagram.
	 */
	diagramContentId?: string;
	editorView?: EditorView;
	isActive: boolean;
	/**
	 * Called once when the board first mounts and its placement is measured — the earliest point a
	 * scroll can target it (before this it isn't in the document).
	 */
	onDiagramBoardReady?: () => void;
	/**
	 * Called once if the diagram board never mounts within the poll window — lets callers report
	 * how often board tracking gives up (which would silently fall back to the shared behaviour).
	 */
	onDiagramBoardTimeout?: () => void;
}): ThinkingWidgetViewportPlacement => {
	const [placement, setPlacement] = useState<ThinkingWidgetViewportPlacement>('in-view');
	// Caches the resolved diagram DOM node so we don't walk the doc on every
	// scroll/resize; re-resolved only once the cached node leaves the document.
	const boardElementRef = useRef<HTMLElement | null>(null);

	useLayoutEffect(() => {
		if (!isActive || !editorView) {
			boardElementRef.current = null;
			setPlacement('in-view');
			return;
		}

		const isDiagram =
			diagramContentId !== undefined &&
			UNSAFE_expValNoExposure('cc_maui_1028_update_diagram_scroll_logic', 'isEnabled', false) ===
				true;

		// For diagrams, track the whiteboard node itself (its position drives the scroll-vs-"View"
		// decision); other remixes track the thinking widget.
		const resolveTrackedElement = (): HTMLElement | null => {
			if (isDiagram) {
				if (boardElementRef.current?.isConnected) {
					return boardElementRef.current;
				}
				const pos = findWhiteboardNodePosByContentId(editorView.state.doc, diagramContentId);
				if (pos !== undefined) {
					const dom = editorView.nodeDOM(pos);
					const boardElement =
						dom instanceof HTMLElement
							? dom
							: dom?.parentElement instanceof HTMLElement
								? dom.parentElement
								: null;
					boardElementRef.current = boardElement;
					return boardElement;
				}
				// The board mounts asynchronously, so it may not be in the document yet — the caller
				// keeps polling until it appears.
				return null;
			}
			return getRemixThinkingWidgetContainer(editorView);
		};

		// Re-resolves the tracked element each call (it can mount/unmount/be replaced mid-remix) and
		// updates placement against the scroll root.
		const runMeasure = (root: HTMLElement) => {
			const element = resolveTrackedElement();
			if (!element) {
				setPlacement('in-view');
				return;
			}
			setPlacement(getViewportPlacement({ element, root }));
		};

		// Nothing to measure when the effect first runs (board mounts async, widget already cleared),
		// so poll until the board mounts, then track it with the usual scroll/resize listeners.
		if (isDiagram) {
			const root = findOverflowScrollParent(editorView.dom) || document.body;
			const measure = () => runMeasure(root);
			let frameId: number | undefined;
			let unbindScroll = () => {};
			let unbindResize = () => {};

			const startTracking = () => {
				measure();
				// The board is now in the document and its placement measured — signal the earliest
				// point a scroll can safely target it.
				onDiagramBoardReady?.();
				const scrollTarget: HTMLElement | Window = root === document.body ? window : root;
				unbindScroll = bind(scrollTarget, {
					type: 'scroll',
					listener: measure,
					options: { passive: true, capture: true },
				});
				unbindResize = bind(window, {
					type: 'resize',
					listener: measure,
					options: { passive: true },
				});
			};

			// ~300 frames ≈ 5s at 60fps — a ceiling for the async insert to land; past it assume a failed
			// insert and stop rather than poll forever. (rAF pauses in background tabs, so no hidden spin.)
			const MAX_POLL_FRAMES = 300;
			let frames = 0;
			const pollForBoard = () => {
				if (resolveTrackedElement()) {
					startTracking();
					return;
				}
				frames += 1;
				if (frames >= MAX_POLL_FRAMES) {
					onDiagramBoardTimeout?.();
					return;
				}
				frameId = requestAnimationFrame(pollForBoard);
			};

			if (resolveTrackedElement()) {
				startTracking();
			} else {
				frameId = requestAnimationFrame(pollForBoard);
			}

			return () => {
				if (frameId !== undefined) {
					cancelAnimationFrame(frameId);
				}
				unbindScroll();
				unbindResize();
				boardElementRef.current = null;
			};
		}

		// Non-diagram: bail out (without querying the scroll parent) when the widget isn't present yet.
		if (!resolveTrackedElement()) {
			setPlacement('in-view');
			return;
		}

		const root = findOverflowScrollParent(editorView.dom) || document.body;
		const measure = () => runMeasure(root);

		measure();

		// Re-measure on scroll (both the overflow scroll parent and the window, to
		// cover page-level scrolling) and on resize.
		const scrollTarget: HTMLElement | Window = root === document.body ? window : root;
		const unbindScroll = bind(scrollTarget, {
			type: 'scroll',
			listener: measure,
			options: { passive: true, capture: true },
		});
		const unbindResize = bind(window, {
			type: 'resize',
			listener: measure,
			options: { passive: true },
		});

		return () => {
			unbindScroll();
			unbindResize();
			boardElementRef.current = null;
		};
	}, [editorView, isActive, diagramContentId, onDiagramBoardReady, onDiagramBoardTimeout]);

	return placement;
};

/**
 * Backwards-compatible wrapper returning a boolean for callers that only need to
 * know whether the widget is in view. Prefer {@link useThinkingWidgetViewportPlacement}
 * when the top/bottom edge is required.
 */
export const useIsThinkingWidgetInViewport = ({
	editorView,
	isComplete,
}: {
	editorView?: EditorView;
	isComplete: boolean;
}): boolean => {
	const placement = useThinkingWidgetViewportPlacement({ editorView, isActive: isComplete });
	return placement === 'in-view';
};
